import React from "react";

/**
 * Farmwatch Logo Component - Version 4
 * Bright yellow sun with fw monogram (three vertical lines + shower head 'f')
 */

export function FarmwatchLogo({ 
  size = 40, 
  variant = "full", // "full", "icon", "wordmark"
  className = "",
  darkMode = false 
}) {
  const sunColor = "#F5B800"; // Bright golden yellow sun
  const fwColor = darkMode ? "#FFFFFF" : "#FFFFFF"; // White fw inside sun
  const textColor = darkMode ? "#FFFFFF" : "#111827";
  const subtextColor = darkMode ? "#9CA3AF" : "#0F766E";

  const iconSize = size;

  const LogoIcon = () => (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      {/* Sun rays radiating outward */}
      <g fill={sunColor}>
        <polygon points="32,0 34,10 30,10"/>
        <polygon points="32,64 34,54 30,54"/>
        <polygon points="0,32 10,34 10,30"/>
        <polygon points="64,32 54,34 54,30"/>
        <polygon points="9.4,9.4 16,14 14,16"/>
        <polygon points="54.6,9.4 48,14 50,16"/>
        <polygon points="9.4,54.6 16,50 14,48"/>
        <polygon points="54.6,54.6 48,50 50,48"/>
        <polygon points="17,3 20,12 15,11"/>
        <polygon points="47,3 44,12 49,11"/>
        <polygon points="17,61 20,52 15,53"/>
        <polygon points="47,61 44,52 49,53"/>
        <polygon points="3,17 12,20 11,15"/>
        <polygon points="61,17 52,20 53,15"/>
        <polygon points="3,47 12,44 11,49"/>
        <polygon points="61,47 52,44 53,49"/>
      </g>
      
      {/* Bright yellow sun circle */}
      <circle cx="32" cy="32" r="22" fill={sunColor}/>
      
      {/* Letter 'f' */}
      <text x="32" y="44" fontFamily="Georgia, serif" fontSize="32" fontWeight="bold" fill={fwColor} textAnchor="middle" opacity="0.95">f</text>
    </svg>
  );

  if (variant === "icon") {
    return <LogoIcon />;
  }

  if (variant === "wordmark") {
    return (
      <div className={`flex flex-col ${className}`}>
        <span 
          className="font-serif font-bold tracking-tight"
          style={{ 
            fontSize: size * 0.6, 
            color: textColor,
            lineHeight: 1.1
          }}
        >
          Farmwatch
        </span>
        <span 
          className="font-mono uppercase tracking-[0.25em]"
          style={{ 
            fontSize: size * 0.18, 
            color: subtextColor,
            marginTop: -2
          }}
        >
          Agricultural Intelligence
        </span>
      </div>
    );
  }

  // Full logo (icon + wordmark)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <LogoIcon />
      <div className="flex flex-col">
        <span 
          className="font-serif font-semibold tracking-tight"
          style={{ 
            fontSize: size * 0.52, 
            color: textColor,
            lineHeight: 1.1
          }}
        >
          Farmwatch
        </span>
        <span 
          className="font-mono uppercase tracking-[0.2em]"
          style={{ 
            fontSize: size * 0.22, 
            color: subtextColor,
            marginTop: -1
          }}
        >
          Agricultural Intelligence
        </span>
      </div>
    </div>
  );
}

/**
 * Chart Watermark Component
 */
export function ChartWatermark({ position = "bottom-right", size = "small" }) {
  const sizeClasses = {
    small: "text-[10px]",
    medium: "text-xs",
    large: "text-sm"
  };

  const positionClasses = {
    "bottom-right": "absolute bottom-2 right-2",
    "bottom-left": "absolute bottom-2 left-2",
    "bottom-center": "absolute bottom-2 left-1/2 -translate-x-1/2",
    "top-right": "absolute top-2 right-2",
  };

  return (
    <div className={`${positionClasses[position]} flex items-center gap-1.5 opacity-60 pointer-events-none`}>
      <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="14" fill="#0F766E" opacity="0.2"/>
        <g fill="#F59E0B">
          <polygon points="16,3 18,8 14,8"/>
          <polygon points="26,6 22,11 20,9"/>
          <polygon points="29,16 24,18 24,14"/>
          <polygon points="26,26 20,23 22,21"/>
          <polygon points="16,29 14,24 18,24"/>
          <polygon points="6,26 12,21 10,23"/>
          <polygon points="3,16 8,14 8,18"/>
          <polygon points="6,6 12,9 10,11"/>
        </g>
        <circle cx="16" cy="16" r="6" fill="#F59E0B"/>
      </svg>
      <span className={`${sizeClasses[size]} font-mono text-[#6B6661] whitespace-nowrap`}>
        Product of Farmwatch.com
      </span>
    </div>
  );
}

/**
 * Logo for dark backgrounds
 */
export function FarmwatchLogoDark({ size = 40, variant = "full" }) {
  return <FarmwatchLogo size={size} variant={variant} darkMode={true} />;
}

export default FarmwatchLogo;
